import { useEffect, useRef } from "react";
import ModelDisplayer from "../modelDisplayer/modeldisplayer";
import { useSelector } from "react-redux";
import { getTestSettings, getUserData } from "../../store/login.slice";
import CAPatientTable from "../CAPatientTable/CApatienttable";
import USPatientTable from "../USPatientTable/uspatienttable";
import PeridontalChart from "../PeriodontalChart/PeriodontalChartLingual";
import Snbchart from "../snbChart/snbchart";
import DentalExaminationChart from "../dentalExaminationChart/dentalexaminationchart";
import MoyerPredictionChart from "../moyerPredictionChart/moyerpredictionchart";
import MoyerInputChart from "../moyerinputchart/moyerinputctart";
import MultipleChoiceAnswerBlock from "../multipleChoiceAnswerBlock/multipleChoiceAnswerBlock";
import PerscriptionAnswerBlock from "../PerscriptionAnswerBlock/perscriptionAnswerBlock";
import DloscePrescriptionAnswerBlock from "../PerscriptionAnswerBlock/dloscePrescriptionAnswerBlock";
import { ErrorBoundary } from "react-error-boundary";
import { AlertCircle } from "lucide-react";
import MarkdownPreview from "../MarkdownPreview/MarkdownPreview";
import styles from "./testCenterColumns.module.scss";
import { TestSettings } from "../../constants/testSettings";

/*
 * Appearance settings from the settings bar are applied here:
 *   splitScreen   two columns (question | answers) or one stacked column
 *   fontFamily    font of everything inside the columns
 *   fontSize      base font size in px …
 *   textZoom      … multiplied by textZoom / 16 (16 = 1x, 19 ≈ 1.19x, 22 ≈ 1.38x)
 *   textAlign     text alignment inside the columns
 *   contentWidth  max width (px) the content can take up inside each column
 * Everything below inherits these through CSS variables, so the child
 * components no longer carry their own text size classes.
 */

// The settings bar's "full" align option is CSS "justify"
const TEXT_ALIGN: Record<string, string> = {
  start: "start",
  center: "center",
  end: "end",
  full: "justify",
};

// USPatientTable still expects Tailwind size classes; empty strings make it
// inherit the size set here instead.
const INHERIT_TEXT_SIZE = { left: "", right: "" };

const UNAVAILABLE_MESSAGE =
  "This question could not be loaded. Please contact support.";

const TestCenterColumns = ({
  centerColumSize,
  currentQuestion,
  questionError = undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getHighlightedText,
  currentQuestionImage,
  questionLoading,
  questionImageLoading,
  processQuestionText,
  GetShuffledAnswerArray,
  setExaminationState,
  examinationState,
  createdExam,
  handleAnswerChecked,
  setScrollChecks,
  ScrollChecks,
  completedAnswerArray,
  questionType,
  imagePath,
  showExplanations,
  showChecksAndXes,
  currentQuestionId,
  ShowMyAnswerMode,
  reviewData,
  prescriptionAnswerData,
  prescriptionAnswerLoading,
}) => {
  const range = 10;
  //
  //Hooks
  //
  const userData = useSelector(getUserData);
  const settings: TestSettings = useSelector(getTestSettings);
  const DLOSCEMode = userData.exam_type;
  const scrollableDivRefLeft = useRef(null);
  const scrollableDivRefRight = useRef(null);
  const scrollableDivRefSingle = useRef(null);
  const splitScreen = settings.splitScreen;
  // DLOSCE customers get dlosceTask on prescription questions (GetQuestionOnly)
  const isDloscePrescription =
    DLOSCEMode == "DLOSCE" &&
    currentQuestion?.GetQuestion?.dlosceTask != undefined;

  const questionUnavailable =
    !questionLoading &&
    (questionError != undefined || currentQuestion?.GetQuestion == undefined);

  //
  //Appearance
  //
  const fontSizePx = settings.fontSize * (settings.textZoom / 16);
  const appearanceStyle = {
    height: centerColumSize,
    "--tc-font-family": settings.fontFamily,
    "--tc-font-size": `${fontSizePx}px`,
    "--tc-text-align": TEXT_ALIGN[settings.textAlign] ?? "start",
    "--tc-content-width":
      settings.contentWidth > 0 ? `${settings.contentWidth}px` : "100%",
  } as React.CSSProperties;
  // MarkdownPreview sizes everything from a 12px base
  const markdownBoost = Math.round((fontSizePx / 12) * 100);

  //
  //Functionality
  //
  const isAtBottom = (div) =>
    Math.abs(div.scrollHeight - div.scrollTop - div.clientHeight) <= range;

  const markScrolled = (sides: { left?: boolean; right?: boolean }) => {
    if (setScrollChecks == undefined) return;
    setScrollChecks((prevState) => {
      const newState = [...prevState];
      newState[examinationState.currentQuestionIndex] = {
        ...newState[examinationState.currentQuestionIndex],
        ...sides,
      };
      return newState;
    });
  };

  const handleScrollLeft = () => {
    const divLeft = scrollableDivRefLeft.current;
    if (divLeft && isAtBottom(divLeft)) markScrolled({ left: true });
  };

  const handleScrollRight = () => {
    const divRight = scrollableDivRefRight.current;
    if (divRight && isAtBottom(divRight)) markScrolled({ right: true });
  };

  // Single column: one scroll area holds both sides, so reaching its bottom
  // counts as having seen both
  const handleScrollSingle = () => {
    const div = scrollableDivRefSingle.current;
    if (div && isAtBottom(div)) markScrolled({ left: true, right: true });
  };

  //
  //useEffect
  //

  // Back to the top whenever the question changes (Previous / Next, question
  // list, navigator), in both the split and single column layouts.
  // currentQuestionId isn't passed in the View Answer modal, so fall back to
  // the loaded question's id.
  const scrollKey =
    currentQuestionId ?? currentQuestion?.GetQuestion?.question_id;
  useEffect(() => {
    [
      scrollableDivRefLeft,
      scrollableDivRefRight,
      scrollableDivRefSingle,
    ].forEach((ref) => {
      if (ref.current) ref.current.scrollTop = 0;
    });
  }, [scrollKey]);

  useEffect(() => {
    if (
      setScrollChecks == undefined ||
      ScrollChecks?.[examinationState.currentQuestionIndex] == undefined
    ) {
      return;
    }
    if (questionUnavailable) {
      markScrolled({ left: true, right: true });
      return;
    }
    if (questionImageLoading || currentQuestion?.GetQuestion == undefined) {
      return;
    }
    const checks = ScrollChecks[examinationState.currentQuestionIndex];
    const fitsWithoutScrolling = (div) =>
      div.scrollHeight - div.scrollTop === div.clientHeight;

    if (!splitScreen) {
      const div = scrollableDivRefSingle.current;
      if (div && (checks.left == false || checks.right == false)) {
        const fits = fitsWithoutScrolling(div);
        markScrolled({ left: fits, right: fits });
      }
      return;
    }

    //Left side has images to account for!
    const divLeft = scrollableDivRefLeft.current;
    if (divLeft && checks.left == false) {
      markScrolled({ left: fitsWithoutScrolling(divLeft) });
    }
    const divRight = scrollableDivRefRight.current;
    if (divRight && checks.right == false) {
      markScrolled({ right: fitsWithoutScrolling(divRight) });
    }
  }, [
    currentQuestion?.GetQuestion,
    questionLoading,
    questionImageLoading,
    questionUnavailable,
    setScrollChecks,
    splitScreen,
  ]);

  //
  //Column contents
  //
  const unavailableNotice = (
    <div className={styles.modelError} role="alert">
      <AlertCircle className={styles.modelErrorIcon} />
      <span>{UNAVAILABLE_MESSAGE}</span>
    </div>
  );

  const questionContent = questionUnavailable ? (
    <div className={styles.content}>{unavailableNotice}</div>
  ) : (
    <div className={styles.content}>
      {!questionLoading && (
        <>
          {DLOSCEMode == "DLOSCE" ? (
            <CAPatientTable
              currentQuestion={currentQuestion}
              getHighlightedText={getHighlightedText}
              theme={userData.theme}
            />
          ) : (
            <USPatientTable
              currentQuestion={currentQuestion}
              textSize={INHERIT_TEXT_SIZE}
              getHighlightedText={getHighlightedText}
            />
          )}
          {/* peridontal */}
          {questionType != undefined && questionType == "Periodontal" && (
            <div className={styles.chartBlock}>
              <PeridontalChart
                extraData={currentQuestion?.GetQuestion.extra_data.vestibular}
                getHighlightedText={getHighlightedText}
                altMode={false}
              />
              <PeridontalChart
                extraData={currentQuestion?.GetQuestion.extra_data.lingual}
                getHighlightedText={getHighlightedText}
                altMode={true}
              />
            </div>
          )}
          {/* SNB */}
          {questionType != undefined && questionType == "SNB" && (
            <div className={styles.chartBlock}>
              <Snbchart
                extraData={currentQuestion?.GetQuestion.extra_data}
                getHighlightedText={getHighlightedText}
              />
            </div>
          )}
          {/* Dental Exam*/}
          {questionType != undefined && questionType == "DentalExam" && (
            <div className={styles.chartBlock}>
              <DentalExaminationChart
                extraData={currentQuestion?.GetQuestion.extra_data.teeth}
                getHighlightedText={getHighlightedText}
              />
            </div>
          )}
          {/* Moyor exam*/}
          {questionType != undefined && questionType == "Moyor" && (
            <div className={styles.chartBlockSpaced}>
              <MoyerPredictionChart getHighlightedText={getHighlightedText} />
              <MoyerInputChart
                getHighlightedText={getHighlightedText}
                extraData={currentQuestion?.GetQuestion.extra_data}
              />
            </div>
          )}
        </>
      )}
      {/* 3d image */}
      <div className={styles.mediaBlock}>
        <ErrorBoundary
          fallback={
            <div className={styles.modelError}>
              <AlertCircle className={styles.modelErrorIcon} />
              <span>
                The model displayer has failed to render, this might be because
                your browser does not have WEBGL enabled. Please enable WEBGL
                and restart this test to view the model.
              </span>
            </div>
          }
        >
          <ModelDisplayer
            imagePath={questionType == "3D" ? imagePath : undefined}
            questionLoading={questionLoading}
            currentQuestionId={currentQuestionId}
          />
        </ErrorBoundary>
      </div>
      {/* Currently loading question */}
      {questionLoading && <span>Loading question...</span>}
      {/* exam image */}
      {currentQuestionImage && !questionImageLoading && (
        <div className={styles.mediaBlock}>
          <img
            src={currentQuestionImage}
            alt="Question"
            className={styles.questionImage}
            onLoad={() => URL.revokeObjectURL(currentQuestionImage)}
          />
        </div>
      )}
      {/* image loading notif */}
      {questionImageLoading && (
        <div className={styles.imageLoading}>
          <span>Loading image...</span>
        </div>
      )}
    </div>
  );

  const answerContent = questionUnavailable ? (
    <div className={styles.answerContent}>{unavailableNotice}</div>
  ) : (
    <div className={styles.answerContent}>
      {/* Currently loading question */}
      {questionLoading && <span>Loading question...</span>}
      {!questionLoading && (
        <>
          {questionType != "Prescription" && (
            <MultipleChoiceAnswerBlock
              processQuestionText={processQuestionText}
              GetShuffledAnswerArray={GetShuffledAnswerArray}
              currentQuestion={currentQuestion}
              completedAnswerArray={completedAnswerArray}
              examinationState={examinationState}
              createdExam={createdExam}
              handleAnswerChecked={handleAnswerChecked}
              getHighlightedText={getHighlightedText}
              showChecksAndXes={showChecksAndXes}
              showExplanations={showExplanations}
              DLOSCEMode={DLOSCEMode}
            />
          )}
          {/* DLOSCE prescription: classification + regimen drop-downs */}
          {questionType == "Prescription" && isDloscePrescription && (
            <DloscePrescriptionAnswerBlock
              dlosceTask={currentQuestion.GetQuestion.dlosceTask}
              examinationState={examinationState}
              setExaminationState={setExaminationState}
              currentQuestionId={currentQuestionId}
              readOnly={ShowMyAnswerMode || reviewData != undefined}
            />
          )}
          {/* NDEB prescription pad */}
          {questionType == "Prescription" && !isDloscePrescription && (
            <PerscriptionAnswerBlock
              examinationState={examinationState}
              setExaminationState={setExaminationState}
              currentQuestionId={currentQuestionId}
              ShowMyAnswerMode={ShowMyAnswerMode}
              reviewData={reviewData}
              reviewMode={reviewData != undefined}
              prescriptionAnswerData={prescriptionAnswerData}
              prescriptionAnswerLoading={prescriptionAnswerLoading}
            />
          )}
          {/* Answer summary - only when the answer is being viewed */}
          {showExplanations && currentQuestion?.GetQuestion?.answer_summary && (
            <div className={styles.explainerBox}>
              <span className={styles.explainerTitle}>Summary</span>
              <span className={styles.preWrap}>
                {currentQuestion.GetQuestion.answer_summary}
              </span>
            </div>
          )}
          {/* Additional explainers - only when the answer is being viewed */}
          {showExplanations &&
            currentQuestion?.GetQuestion?.additional_explainers?.length > 0 && (
              <div className={styles.explainerBox}>
                <span className={styles.explainerTitle}>
                  Additional Explainers
                </span>
                <div className={styles.explainerList}>
                  {currentQuestion.GetQuestion.additional_explainers.map(
                    (explainer, index) => (
                      <div
                        key={`explainer-${index}`}
                        className={styles.explainerItem}
                      >
                        <MarkdownPreview
                          fontSizeBoost={markdownBoost}
                          preserveLineBreaks={true}
                        >
                          {explainer}
                        </MarkdownPreview>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );

  if (!splitScreen) {
    return (
      <div
        className={`${styles.columns} ${styles.single}`}
        style={appearanceStyle}
        ref={scrollableDivRefSingle}
        onScroll={handleScrollSingle}
      >
        <div className={styles.singleColumn}>
          {questionContent}
          <div className={styles.singleDivider} />
          {answerContent}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.columns} ${styles.split}`}
      style={appearanceStyle}
    >
      {/* left column */}
      <div
        className={styles.leftColumn}
        ref={scrollableDivRefLeft}
        onScroll={handleScrollLeft}
      >
        {questionContent}
      </div>
      {/* right column (scrollbar on the left side) */}
      <div
        className={`${styles.rightColumn} scrollbar-left`}
        ref={scrollableDivRefRight}
        onScroll={handleScrollRight}
      >
        <div className="leftReset">{answerContent}</div>
      </div>
    </div>
  );
};

export default TestCenterColumns;
